import { useValue } from "@legendapp/state/react";
import type { CatalogBoardSuggestion } from "@repo/db/schema";
import { Button } from "@repo/ui/button";
import { type BaseIconName, Icon } from "@repo/ui/components/icon";
import { Tooltip, TooltipContent, TooltipTrigger } from "@repo/ui/tooltip";
import { useRouterState } from "@tanstack/react-router";
import { useMemo } from "react";

import { catalogs$ } from "~/stores/catalogs";
import { datasets$ } from "~/stores/datasets";

/** Picks `count` random items from `items` using a seeded shuffle. */
const pickRandom = <T,>(items: T[], count: number, seed: number): T[] => {
  if (items.length <= count) return items;
  const shuffled = [...items];
  let s = seed;
  for (let i = shuffled.length - 1; i > 0; i--) {
    s = (s * 16807 + 0) % 2147483647;
    const j = s % (i + 1);
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled.slice(0, count);
};

const VISIBLE_SUGGESTIONS = 5;

/** Dataset-scoped suggested prompts derived from catalog suggestions. */
const DatasetSuggestedPrompts = ({
  datasetId,
  onSend,
}: {
  datasetId: string;
  onSend: (prompt: string) => void;
}) => {
  const allSuggestions = useValue(() => {
    const dsId = datasets$[datasetId].datasourceId.get();
    return dsId ? catalogs$[dsId].suggestions.get() : undefined;
  });

  const seed = useMemo(() => Date.now(), []);

  const visible = useMemo(
    () =>
      allSuggestions
        ? pickRandom(allSuggestions, VISIBLE_SUGGESTIONS, seed)
        : [],
    [allSuggestions, seed],
  );

  if (!visible.length) return null;

  return (
    <>
      {visible.map((s) => (
        <Tooltip key={s.title}>
          <TooltipTrigger
            render={
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onSend(s.prompt)}
                className="justify-start"
              >
                <Icon name={s.icon as BaseIconName} className="size-3.5" />
                <span className="line-clamp-1">{s.title}</span>
              </Button>
            }
          />
          <TooltipContent
            side="left"
            className="wrap-break-word max-h-32 max-w-sm overflow-y-auto"
          >
            {s.prompt}
          </TooltipContent>
        </Tooltip>
      ))}
    </>
  );
};

/**
 * Board-scoped suggested prompts derived from the generated dashboard
 * suggestions (`boardSuggestions`) flattened across every available
 * datasource catalog. Shows a random assortment so the board chat surfaces
 * ideas from across the whole warehouse rather than a fixed set. Falls back
 * to the static prompts until any catalog has dashboard suggestions.
 */
const BoardSuggestedPrompts = ({
  onSend,
}: {
  onSend: (prompt: string) => void;
}) => {
  const allSuggestions = useValue(() =>
    Object.values(catalogs$.get() ?? {}).flatMap(
      (c) => c?.boardSuggestions ?? [],
    ),
  ) as CatalogBoardSuggestion[];

  const seed = useMemo(() => Date.now(), []);

  const visible = useMemo(
    () => pickRandom(allSuggestions, VISIBLE_SUGGESTIONS, seed),
    [allSuggestions, seed],
  );

  if (!visible.length) return null;

  return (
    <>
      {visible.map((s) => (
        <Tooltip key={`${s.title}-${s.description}`}>
          <TooltipTrigger
            render={
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onSend(`${s.title} — ${s.description}`)}
                className="justify-start"
              >
                <Icon
                  name={(s.icon as BaseIconName) ?? "data-bar-chart"}
                  className="size-3.5"
                />
                <span className="line-clamp-1">{s.title}</span>
              </Button>
            }
          />
          <TooltipContent
            side="left"
            className="wrap-break-word max-h-32 max-w-sm overflow-y-auto"
          >
            {s.description}
          </TooltipContent>
        </Tooltip>
      ))}
    </>
  );
};

/** Context-aware suggested prompts shown when the chat has no messages. */
export const SuggestedPrompts = ({
  onSend,
}: {
  onSend: (prompt: string) => void;
}) => {
  const datasetId = useRouterState({
    select: (s) => s.location.pathname.match(/\/datasets\/([^/]+)/)?.[1],
  });

  return (
    <div className="mx-3 mb-1.5 flex flex-col gap-1">
      {datasetId ? (
        <DatasetSuggestedPrompts datasetId={datasetId} onSend={onSend} />
      ) : (
        <BoardSuggestedPrompts onSend={onSend} />
      )}
    </div>
  );
};
